import React, { useEffect, useState } from "react";
import { Row, Container, Col, Card, CardHeader, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import SearchInput from "../Common/SearchInput";
import { useRecoilState } from "recoil";
import useUrlState from "@ahooksjs/use-url-state";
import { resetFormPromptState } from "src/recoil/atoms/global";
import { PAGINATION_DEFAULT, ROLES } from "src/constant/common";
import { CustomLink } from "../Common/CustomLink";
import APP_ROUTES from "src/routes/routes";
import { handleScrollToTop } from "src/utils/common";
import TableCommon from "../Common/Table";
import { columns } from "./columns";
import ModalDeployInstance from "../Common/Modal/ModalDeployInstance";
import useGetProjectInstanceList from "./useGetProjectInstanceList";
import ProtectedComponent from "../Permission";

const ProjectInstanceList = () => {
  const { t } = useTranslation(["project-instance", "common"]);

  const [resetForm, setResetForm] = useRecoilState(resetFormPromptState);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isLoadingProjectInstance, setIsLoadingProjectInstance] = useState(true);

  const [params, setParams] = useUrlState(
    {},
    {
      navigateMode: "replace",
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );

  const [textSearch, setTextSearch] = useState(params?.condition || null);

  const { projectInstanceList, projectInstanceListRefetch } = useGetProjectInstanceList(
    {
      page: params.page,
      limit: PAGINATION_DEFAULT.pageSize,
      q: params?.condition,
      id: params?.id,
    },
    () => setIsLoadingProjectInstance(false)
  );

  const onKeyDownInput = (e) => {
    if (e.key === "Enter") {
      onSubmitSearch(e.target.value);
    }
  };

  const onSubmitSearch = (textSearch) => {
    if (params?.condition === textSearch && !isLoadingProjectInstance) {
      setIsLoadingProjectInstance(true);
    }
    setParams({
      page: 1,
      condition: textSearch,
    });
  };

  const handleDeployClick = (instanceData) => {
    setSelectedInstance(instanceData);
    setIsDeployModalOpen(true);
  };

  const getNewInstanceUrl = (page, condition) => {
    const params = new URLSearchParams();

    if (page) params.set("page", page);
    if (condition) params.set("condition", condition);

    const queryString = params.toString();
    return `${APP_ROUTES.INSTANCE_NEW}${queryString ? "?" + queryString : ""}`;
  };

  const redirectToTestPackageList = (testPackageId) => {
    if (!testPackageId) return;
    window.open(`${APP_ROUTES.TEST_PACKAGE}?instanceId=${testPackageId}&projectId=${params?.projectId}`, "_blank");
  };

  const handleCloseModal = () => {
    setIsDeployModalOpen(false);
    setSelectedInstance(null);
  };

  const getCurrentPage = () => {
    return (params?.page || PAGINATION_DEFAULT.page) - 1;
  };

  const onChangePage = (value) => {
    setParams({
      ...params,
      page: value,
    });
    handleScrollToTop();
  };

  useEffect(() => {
    setIsLoadingProjectInstance(true);
  }, [params]);

  useEffect(() => {
    if (resetForm) {
      setTextSearch(null);
      setResetForm(false);
      handleScrollToTop();
    }
  }, [resetForm]);

  return (
    <Container fluid>
      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="header-title mb-0">{t("list.title")}</h2>
                <SearchInput
                  submit={onSubmitSearch}
                  setTextSearch={setTextSearch}
                  onKeyDownInput={onKeyDownInput}
                  textSearch={textSearch}
                  placeholder={t("list.search_placeholder")}
                />
              </div>
              <Row>
                <Col md={12}>
                  <Card>
                    <div className="d-flex justify-content-end">
                      <CardHeader className="bg-white border-0">
                        <ProtectedComponent id="project_instance_create_new" requiredRole={ROLES.SERVICE_ADMIN || ROLES.ADMIN}>
                          <CustomLink to={getNewInstanceUrl(params?.page, params?.condition)} className="btn btn-primary text-capitalize">
                            {t("list.redirect_new")}
                          </CustomLink>
                        </ProtectedComponent>
                      </CardHeader>
                    </div>
                    <CardBody className="bg-white">
                      <TableCommon
                        loading={isLoadingProjectInstance}
                        columns={columns({ t, handleDeployClick, params, redirectToTestPackageList })}
                        data={projectInstanceList?.items || []}
                        onChangePage={onChangePage}
                        tableFixed
                        tableClassName="table striped table-collapse table-fixed"
                        customClassName="overflow-auto project-instance-table"
                        pageCurrent={getCurrentPage()}
                        pageCount={projectInstanceList?.pagination?.total_page}
                        totalItem={projectInstanceList?.pagination?.total}
                        pageSize={PAGINATION_DEFAULT.pageSize}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Row>

      {isDeployModalOpen && (
        <ModalDeployInstance
          instanceData={selectedInstance}
          projectInstanceListRefetch={projectInstanceListRefetch}
          isOpen={isDeployModalOpen}
          toggle={handleCloseModal}
          setIsLoadingProjectInstance={setIsLoadingProjectInstance}
        />
      )}
    </Container>
  );
};

export default ProjectInstanceList;
